import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Clock, AlertTriangle, ArrowRight } from "lucide-react";
import api from "../lib/api";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const pfBookingId = params.get("provider") === "payfast" ? params.get("booking_id") : null;
  const providerName = pfBookingId ? "PayFast" : "Stripe";
  const [state, setState] = useState({ status: "checking", payment_status: null, booking_id: null });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId && !pfBookingId) return;
    let cancelled = false;
    const maxAttempts = pfBookingId ? 15 : 8; // PayFast ITN can lag a little behind the browser redirect
    const poll = async (n = 0) => {
      if (n >= maxAttempts || cancelled) return setState((s) => ({ ...s, status: s.payment_status === "paid" ? "complete" : "timeout" }));
      try {
        const { data } = await api.get(pfBookingId ? `/payments/payfast/status/${pfBookingId}` : `/payments/status/${sessionId}`);
        setAttempts(n + 1);
        if (data.payment_status === "paid") {
          setState({ status: "complete", payment_status: "paid", booking_id: data.booking_id });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", payment_status: data.payment_status, booking_id: data.booking_id });
          return;
        }
        setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        setTimeout(() => poll(n + 1), 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, pfBookingId]);

  return (
    <div className="relative min-h-[calc(100vh-4rem)]">
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        {state.status === "checking" && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#1d7ffd]/10 text-[#1d7ffd] mx-auto flex items-center justify-center">
              <Clock className="animate-pulse" />
            </div>
            <h1 className="font-display text-3xl font-bold text-[#08153a] mt-6">Confirming your payment…</h1>
            <p className="text-sm text-[#64748B] mt-2">Checking with {providerName} (attempt {attempts || 1}/{pfBookingId ? 15 : 8}). Please don't close this tab.</p>
          </>
        )}
        {state.status === "complete" && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#10B981]/15 text-[#10B981] mx-auto flex items-center justify-center">
              <Check size={28} />
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-[#08153a] mt-6">Booking confirmed</h1>
            <p className="text-[#334155] mt-3 leading-relaxed">Your trip is locked in. We've sent the details to your dashboard along with the host's contact info.</p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <Link to="/dashboard" className="btn-primary text-sm" data-testid="success-dashboard-cta">Go to dashboard <ArrowRight size={16} /></Link>
              <Link to="/vehicles" className="btn-outline text-sm">Book another</Link>
            </div>
          </>
        )}
        {state.status === "expired" && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#EF4444]/15 text-[#EF4444] mx-auto flex items-center justify-center">
              <AlertTriangle />
            </div>
            <h1 className="font-display text-3xl font-bold text-[#08153a] mt-6">Payment session expired</h1>
            <p className="text-[#334155] mt-3">No charges were made. You can try again from the car page.</p>
            <Link to="/vehicles" className="btn-primary text-sm mt-6 inline-flex">Browse cars</Link>
          </>
        )}
        {state.status === "timeout" && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#F59E0B]/15 text-[#F59E0B] mx-auto flex items-center justify-center">
              <Clock />
            </div>
            <h1 className="font-display text-3xl font-bold text-[#08153a] mt-6">Still processing</h1>
            <p className="text-[#334155] mt-3">{providerName} is taking a little longer than usual. Check your dashboard in a minute.</p>
            <Link to="/dashboard" className="btn-primary text-sm mt-6 inline-flex">Open dashboard <ArrowRight size={16} /></Link>
          </>
        )}
      </div>
    </div>
  );
}
